import { FunctionComponent } from 'react'
import {
  Box,
  TableCell,
  TableRow,
  Typography,
} from '@black-bear-energy/rucksack'
import {
  ChecklistDocument,
  ChecklistDocumentAction,
} from '@black-bear-energy/black-bear-energy-common'
import { DateAndTime } from './DateAndTime'
import { DocumentStatusChip } from './DocumentStatusChip'
import { DocumentActionShortcut } from './DocumentActionShortcut'
import { ReviewComment } from './ReviewComment'
import { TextWithTooltip } from '../common/TextWithTooltip'
import { DocumentMenu } from './DocumentMenu'
import { InlineFileUploader } from './InlineFileUploader'

export const DocumentSection: FunctionComponent<DocumentSectionProps> = ({
  document,
  isLimitedUser,
  isNested = false,
  searchString,
  isCollectionDocument = false,
}: DocumentSectionProps) => {
  return (
    <TableRow data-testid="documentSection" sx={{ position: 'relative' }}>
      <TableCell style={isNested ? { paddingLeft: '40px' } : {}}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box sx={{ width: '40px', height: '40px', flexShrink: 0 }} />
          <TextWithTooltip text={document.name} searchString={searchString} />
          {!document.uploaded && (
            <InlineFileUploader
              documentId={document.id}
              documentName={document.name}
            />
          )}
        </Box>
      </TableCell>
      <TableCell style={isNested ? { paddingLeft: '40px' } : {}}>
        <Box display="flex" alignItems="flex-start" paddingLeft="16px">
          <DocumentStatusChip document={document} />
        </Box>
      </TableCell>
      <TableCell />
      <TableCell>
        <Box display="flex" justifyContent="center">
          <DocumentActionShortcut
            document={document}
            isLimitedUser={isLimitedUser}
          />
        </Box>
      </TableCell>
      <TableCell>
        <Box display="flex" justifyContent="center">
          <ReviewComment document={document} />
        </Box>
      </TableCell>
      <TableCell>
        {document.updatedBy && document.lastAction && (
          <>
            <Typography variant="body2">
              {getUpdateTextPrefix(document.lastAction)}
            </Typography>
            <TextWithTooltip
              text={document.updatedBy}
              variant="body2"
              maxLineCount={1}
              searchString={searchString}
            />
          </>
        )}
      </TableCell>
      <TableCell>
        {document.updatedBy && <DateAndTime date={document.lastUpdated} />}
      </TableCell>
      <TableCell>
        <DocumentMenu
          documentId={document.id}
          documentName={document.name}
          isUploaded={document.uploaded}
          hasReview={document.review != null}
          isLimitedUser={isLimitedUser}
          isCollectionDocument={isCollectionDocument}
        />
      </TableCell>
    </TableRow>
  )
}

DocumentSection.displayName = 'DocumentSection'

interface DocumentSectionProps {
  document: ChecklistDocument
  isLimitedUser: boolean
  isNested?: boolean
  searchString?: string
  /** True if this document is a member of a document collection. */
  isCollectionDocument?: boolean
}

function getUpdateTextPrefix(lastAction: ChecklistDocumentAction): string {
  switch (lastAction) {
    case ChecklistDocumentAction.Uploaded:
      return 'Uploaded by'
    case ChecklistDocumentAction.Reviewed:
      return 'Reviewed by'
    case ChecklistDocumentAction.Deleted:
      return 'Deleted by'
  }
}
